/**
 * Shared read model for the Fridge.com Refrigerator Price by Size cross-section.
 *
 * Single source of truth for the page (app/data/price-by-size/page.tsx), the
 * JSON-LD schema props, the CSV route (app/data/price-by-size/csv/route.ts), and
 * the share-card image route (app/data/price-by-size/card/route.tsx), so the
 * on-page stat grid, the size-class table, the by-type table, the structured
 * data, and every download cite the SAME figures from
 * app/data/_data/price-by-size.json.
 *
 * INTEGRITY: every number here is read directly from that JSON, which is itself
 * a frozen snapshot of a dollar-price cross-section derived from the live
 * Fridge.com catalog — active, listable products that publish BOTH a cubic-foot
 * capacity and a valid advertised price. Nothing is invented or narrated. A
 * figure that cannot be honestly derived for a size class or type (fewer than
 * the suppression minimum) is null and rendered as absent — never estimated.
 */
import data from '@/app/data/_data/price-by-size.json'
import { formatPrice } from '@/lib/format-utils'

export interface SizeBucketRow {
  /** Machine key for the cubic-foot band, e.g. '0-5', '30+'. */
  bucket: string
  /** Display label, e.g. '0-5 cu ft'. */
  label: string
  /** Qualifying products in the band (both capacity and in-range price). */
  n: number
  /** 10th-percentile advertised price (USD). */
  p10_price: number
  /** Median advertised price (USD). */
  median_price: number
  /** 90th-percentile advertised price (USD). */
  p90_price: number
  /** Lowest qualifying advertised price in the band (USD). */
  min_price: number
  /** Highest qualifying advertised price in the band (USD). */
  max_price: number
}

export interface TypeRow {
  type: string
  label: string
  n: number
  /** Median cubic-foot capacity for the type — null below the suppression min. */
  median_cuft: number | null
  p10_price: number | null
  median_price: number | null
  p90_price: number | null
  /** True once the type clears the suppression minimum and its figures publish. */
  published: boolean
}

export interface PriceBySizeCoverage {
  active_listable_products: number
  with_capacity: number
  with_price: number
  with_both: number
  analysis_set: number
  trimmed_outliers: number
}

export interface PriceBySizeOverall {
  n: number
  median_price: number
  p10_price: number
  p90_price: number
  min_price: number
  max_price: number
  median_cuft: number
  min_cuft: number
  max_cuft: number
  ratio_largest_to_smallest: number
}

export interface PriceBySizeMeta {
  methodology_version: string
  computed_at: string
  source_program: string
  source_authority: string
  synthesis_note: string
  suppression_min_n: number
  price_gate_usd: [number, number]
  capacity_gate_cuft: number
  coverage: PriceBySizeCoverage
  limitations: string[]
}

export const priceBySizeMeta = data.meta as unknown as PriceBySizeMeta
export const priceBySizeOverall = data.overall as unknown as PriceBySizeOverall
export const priceBySizeBuckets = data.buckets as unknown as SizeBucketRow[]
export const priceBySizeTypes = data.types as unknown as TypeRow[]

export const suppressionMinN = priceBySizeMeta.suppression_min_n ?? 8

/** Published appliance types (n ≥ the suppression minimum), priciest first. */
export function publishedTypeRows(): TypeRow[] {
  return priceBySizeTypes
    .filter((t) => t.published)
    .sort((a, b) => (b.median_price ?? 0) - (a.median_price ?? 0))
}

/** Types disclosed but not published (below the suppression minimum), largest first. */
export function belowThresholdTypeRows(): TypeRow[] {
  return priceBySizeTypes.filter((t) => !t.published).sort((a, b) => b.n - a.n)
}

/** Total products across the below-threshold types (disclosed, not published). */
export function belowThresholdProductCount(): number {
  return belowThresholdTypeRows().reduce((sum, t) => sum + t.n, 0)
}

/**
 * The cheapest and priciest size classes by median advertised price — the two
 * ends of the cross-section. Both are published bands with real samples.
 */
export function bucketExtremes(): { cheapest: SizeBucketRow; priciest: SizeBucketRow } {
  const sorted = [...priceBySizeBuckets].sort((a, b) => a.median_price - b.median_price)
  return { cheapest: sorted[0], priciest: sorted[sorted.length - 1] }
}

/**
 * Among published types, the highest- and lowest-priced by median — an honest
 * anchor for the insight callout.
 */
export function typePriceExtremes(): { high: TypeRow | null; low: TypeRow | null } {
  const pub = publishedTypeRows()
  if (pub.length === 0) return { high: null, low: null }
  return { high: pub[0], low: pub[pub.length - 1] }
}

/** The published type with the widest 10th-to-90th-percentile price spread. */
export function widestTypeSpread(): TypeRow | null {
  const pub = publishedTypeRows().filter((t) => t.p10_price !== null && t.p90_price !== null)
  if (pub.length === 0) return null
  return [...pub].sort(
    (a, b) => (b.p90_price! - b.p10_price!) - (a.p90_price! - a.p10_price!)
  )[0]
}

/**
 * The size class with the widest relative spread (P90 ÷ P10) — the band where a
 * single cubic-foot figure hides the most price variation.
 */
export function widestBucketByRatio(): SizeBucketRow {
  return [...priceBySizeBuckets].sort(
    (a, b) => b.p90_price / b.p10_price - a.p90_price / a.p10_price
  )[0]
}

/**
 * Advertised-price display. Canonical rule (owner ruling 07/25/2026): whole
 * dollars drop ".00", real cents render exactly — never rounded or truncated.
 * Shared implementation in lib/format-utils.
 */
export function formatUsd(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return '—'
  return formatPrice(usd)
}

/** '$149–$1,417' price range; an em dash when either end is absent. */
export function formatUsdRange(p10: number | null, p90: number | null): string {
  if (p10 === null || p90 === null) return '—'
  return `${formatUsd(p10)}–${formatUsd(p90)}`
}

/** Cubic-foot capacity, trailing zeros trimmed, at most 2 decimals. */
export function formatCuft(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return v.toFixed(2).replace(/\.?0+$/, '')
}

/** 'YYYY-MM-DD' → 'July 2026'. Parsed as UTC to avoid timezone drift. */
export function formatDateLabel(date: string | null | undefined): string {
  if (!date) return 'n/a'
  const [y, m] = date.split('-').map(Number)
  if (!y || !m) return date
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Public methodology label, e.g. "v1.0" — released major.minor only. */
export const priceBySizeMethodologyVersionLabel: string = (() => {
  const raw = priceBySizeMeta.methodology_version ?? ''
  const core = raw.split('-')[0]
  const parts = core.split('.').filter(Boolean)
  if (parts.length >= 2) return `v${parts[0]}.${parts[1]}`
  return core ? `v${core}` : 'n/a'
})()

/**
 * Canonical FAQ set, drawn from the live numbers. Shared by the FAQPage JSON-LD
 * and the on-page FAQ so the structured data always matches the rendered text.
 */
export function buildPriceBySizeFAQs(): Array<{ question: string; answer: string }> {
  const o = priceBySizeOverall
  const c = priceBySizeMeta.coverage
  const ex = bucketExtremes()
  const pub = publishedTypeRows().length
  return [
    {
      question: 'What is Refrigerator Price by Size?',
      answer: `It is a dollar-price cross-section from Fridge.com: the median advertised price of a refrigerator or freezer sliced by cubic-foot size class and by appliance type. Across ${o.n.toLocaleString('en-US')} products that publish both a capacity and a price, the median runs from ${formatUsd(ex.cheapest.median_price)} in the ${ex.cheapest.label} class to ${formatUsd(ex.priciest.median_price)} in the ${ex.priciest.label} class. Each figure carries a 10th-to-90th-percentile range so the cheapest and priciest tenth never distort it.`,
    },
    {
      question: 'How much does a refrigerator cost by size?',
      answer: `Bigger costs more, but not evenly. The median advertised price is ${formatUsd(priceBySizeBuckets[0].median_price)} for ${priceBySizeBuckets[0].label}, ${formatUsd(priceBySizeBuckets[3].median_price)} for ${priceBySizeBuckets[3].label}, and ${formatUsd(priceBySizeBuckets[4].median_price)} for ${priceBySizeBuckets[4].label} — the sharpest step is into the 20-25 cu ft class, where full-size French-door and side-by-side designs take over. The largest class runs ${o.ratio_largest_to_smallest}× the smallest at the median.`,
    },
    {
      question: 'How is the cross-section built?',
      answer: `The universe is the active, listable Fridge.com catalog restricted to products that publish both a cubic-foot capacity and a valid advertised price. Prices are trimmed to a $${priceBySizeMeta.price_gate_usd[0]}-$${priceBySizeMeta.price_gate_usd[1].toLocaleString('en-US')} validity gate and capacity to ${priceBySizeMeta.capacity_gate_cuft} cubic feet, then the median and the 10th/90th percentiles are taken for each size class and each type. The aggregation is the Fridge.com synthesis.`,
    },
    {
      question: 'Why is it based on 1,252 products and not the whole catalog?',
      answer: `Because a size cross-section needs a published capacity. Of ${c.active_listable_products.toLocaleString('en-US')} active listings, ${c.with_capacity.toLocaleString('en-US')} publish a cubic-foot capacity and ${c.analysis_set.toLocaleString('en-US')} publish both a capacity and an in-range advertised price. The figures describe that measured subset, and every table states it plainly rather than implying full coverage.`,
    },
    {
      question: 'Why do some appliance types show no price?',
      answer: `A type is published only once it carries at least ${suppressionMinN} qualifying products — ${pub} of the ${priceBySizeTypes.length} types on Fridge.com clear that bar. The rest are disclosed with their product count but no price figure, because a median over a handful of listings is not a benchmark. A blank cell means "not published," never zero.`,
    },
    {
      question: 'How current is this, and can I download it?',
      answer: `This release, methodology ${priceBySizeMethodologyVersionLabel}, reflects the catalog as of ${formatDateLabel(priceBySizeMeta.computed_at)}. Advertised prices move with the market, so the figures are a dated snapshot, not live quotes. The full cross-section is available as a CSV at https://fridge.com/data/price-by-size/csv, and the method is documented on the page.`,
    },
  ]
}
